import { inject, Injectable } from '@angular/core';
import { LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import { UrlParamsService } from '@shared/services/url-params';

/** Query-string parameter carrying the kitchen personalization URL when a visitor
 *  lands on the appointment funnel from the external configurator. */
const CONFIGURATOR_URL_QUERY_PARAM = 'configuratorUrl';

/** sessionStorage key keeping the captured configurator URL alive across the
 *  (route-based) funnel steps and page refreshes, until the appointment is created. */
const CONFIGURATOR_URL_STORAGE_KEY = 'socooc.rdv.configuratorUrl';

/** CMS page type shared by every appointment-funnel page (steps 1-3 + confirmation). */
const FUNNEL_PAGE_TYPE = 'tunnel';

/**
 * Carries the kitchen personalization (configurator) URL from the funnel entry
 * query parameter to the `POST /rdvs` payload (`project` field).
 *
 * The URL is captured once at funnel entry, stripped from the address bar, and kept
 * in sessionStorage so it survives the route-based funnel navigation and a page
 * refresh. It is read when the appointment is submitted, then cleared.
 *
 * The external configurator must pass the value url-encoded (`encodeURIComponent`);
 * Angular decodes query parameters automatically, so the value read here is already
 * the plain URL.
 */
@Injectable({ providedIn: 'root' })
export class ConfiguratorLinkService {
  private readonly urlParams = inject(UrlParamsService);
  private readonly platform = inject(PlatformContextService);
  private readonly logs = inject(LogsService);

  /**
   * Reads the configurator URL from the entry query parameter, persists it, then
   * removes the parameter from the address bar. No-op on the server and when the
   * parameter is absent. Only `https:` URLs are stored (guards against `javascript:`
   * or junk values that could later be rendered as a clickable link in the back-office).
   */
  captureFromUrl(): void {
    if (!this.platform.isBrowser()) {
      return;
    }
    const rawValue = this.urlParams.param(CONFIGURATOR_URL_QUERY_PARAM)();
    if (!rawValue) {
      return;
    }
    if (this.isHttpsUrl(rawValue)) {
      sessionStorage.setItem(CONFIGURATOR_URL_STORAGE_KEY, rawValue);
    } else {
      this.logs.warn('ConfiguratorLinkService', 'Ignored non-https configurator URL');
    }
    // Always strip the parameter, valid or not, to keep the address bar clean.
    this.urlParams.removeParams(CONFIGURATOR_URL_QUERY_PARAM);
  }

  /** Returns the persisted configurator URL, or `null` when none was captured. */
  read(): string | null {
    if (!this.platform.isBrowser()) {
      return null;
    }
    return sessionStorage.getItem(CONFIGURATOR_URL_STORAGE_KEY);
  }

  /** Clears the persisted configurator URL, once the appointment has been created. */
  clear(): void {
    if (!this.platform.isBrowser()) {
      return;
    }
    sessionStorage.removeItem(CONFIGURATOR_URL_STORAGE_KEY);
  }

  /**
   * Drops the persisted configurator URL as soon as the visitor leaves the appointment
   * funnel. Called for every resolved page: a no-op while the visitor stays on funnel
   * pages (`type === 'tunnel'`), so the URL survives step navigation and refreshes, and
   * is cleared the moment they navigate to any non-funnel page.
   */
  clearIfLeftFunnel(pageType: string): void {
    if (pageType === FUNNEL_PAGE_TYPE) {
      return;
    }
    this.clear();
  }

  private isHttpsUrl(value: string): boolean {
    try {
      return new URL(value).protocol === 'https:';
    } catch {
      return false;
    }
  }
}
